import { formatVehicleDisplayTitle, SectionRule, formatChf, MarketPositionScale } from './StockVehiclePanel'
import { cotationReferenceLabel } from '../utils/cotationReference'
import { MarchePanelInsights } from './MarchePanelInsights'
import { useBackToClose } from '../hooks/useBackToClose'

const RADIUS_BLOCK = 6
const RADIUS_PILL = 4

function getMarchePriceStepLabels(history) {
  if (!Array.isArray(history) || history.length === 0) return []
  const values = []
  for (const step of history) {
    const oldP = Number(step?.ancien_prix)
    const newP = Number(step?.nouveau_prix)
    if (Number.isFinite(oldP) && values.length === 0) values.push(oldP)
    if (Number.isFinite(newP)) values.push(newP)
  }
  const compact = values.filter((v, i) => i === 0 || v !== values[i - 1])
  return compact.map((v) => `${Number(v).toLocaleString('fr-CH')} CHF`)
}

function formatMarchePriceChainCompact(history) {
  const steps = getMarchePriceStepLabels(history)
  if (steps.length < 2) return null
  if (steps.length <= 3) return steps.join(' à ')
  return `${steps[0]} … ${steps[steps.length - 1]}`
}

function formatMarcheHistDate(iso) {
  if (!iso) return null
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return null
  return new Date(t).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short', year: 'numeric' })
}

function getMarcheHistorySteps(history) {
  if (!Array.isArray(history) || history.length === 0) return []
  return history.map((step, idx) => {
    const ap = step?.ancien_prix
    const np = step?.nouveau_prix
    const dRaw = step?.delta
    const d =
      dRaw != null && Number.isFinite(Number(dRaw))
        ? Number(dRaw)
        : Number.isFinite(Number(np)) && Number.isFinite(Number(ap))
          ? Number(np) - Number(ap)
          : NaN
    return {
      n: idx + 1,
      from:
        ap != null && Number.isFinite(Number(ap)) ? `${Number(ap).toLocaleString('fr-CH')} CHF` : '…',
      to: np != null && Number.isFinite(Number(np)) ? `${Number(np).toLocaleString('fr-CH')} CHF` : '…',
      delta: Number.isFinite(d) ? `${d > 0 ? '+' : ''}${Number(d).toLocaleString('fr-CH')} CHF` : '…',
      isDrop: Number.isFinite(d) && d < 0,
      dateLabel: formatMarcheHistDate(step?.cree_at),
    }
  })
}

function countMarchePriceDrops(history) {
  if (!Array.isArray(history) || history.length === 0) return 0
  let n = 0
  for (const step of history) {
    const ap = step?.ancien_prix
    const np = step?.nouveau_prix
    const dRaw = step?.delta
    const d =
      dRaw != null && Number.isFinite(Number(dRaw))
        ? Number(dRaw)
        : Number.isFinite(Number(np)) && Number.isFinite(Number(ap))
          ? Number(np) - Number(ap)
          : NaN
    if (Number.isFinite(d) && d < 0) n++
  }
  return n
}

function scaleFromFourchetteRecord(r) {
  if (!r) return null
  const lo = Number(r.fourchette_basse)
  const hi = Number(r.fourchette_haute)
  let median = Number(r.valeur_estimee)
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return null
  if (!Number.isFinite(median)) median = (lo + hi) / 2
  median = Math.min(hi, Math.max(lo, median))
  return { lo, hi, median }
}

function ecartMarchePillStyle(ec) {
  if (!Number.isFinite(ec)) return { color: 'var(--text-secondary)', border: '1px solid rgba(148,163,184,0.35)', bg: 'transparent' }
  if (ec > 8) return { color: '#e7a39a', border: '1px solid rgba(248,113,113,0.35)', bg: 'rgba(248,113,113,0.06)' }
  if (ec < -8) return { color: '#9ec5f7', border: '1px solid rgba(96,165,250,0.35)', bg: 'rgba(96,165,250,0.06)' }
  return { color: 'var(--text-secondary)', border: '1px solid rgba(148,163,184,0.28)', bg: 'rgba(255,255,255,0.02)' }
}

function marcheBaisseAlert(record) {
  const n = countMarchePriceDrops(record?.price_history)
  if (n >= 3) {
    return {
      title: 'Vendeur probablement motivé',
      body: `${n} baisses cumulées sur l’historique — marge de négociation possible.`,
      border: '2px solid #A07530',
      bg: 'rgba(224,184,120,0.08)',
    }
  }
  const d = Number(record?.delta)
  if (Number.isFinite(d) && d < -400) {
    return {
      title: 'Baisse marquée',
      body: 'Dernier mouvement : écart significatif par rapport au prix affiché précédemment.',
      border: '2px solid rgba(248,113,113,0.45)',
      bg: 'rgba(248,113,113,0.06)',
    }
  }
  return null
}

/**
 * Panneau latéral Marché — même hiérarchie visuelle que Mon Stock (header sticky, corps scroll, sections).
 */
export function MarcheVehiclePanel({
  token,
  record: r,
  variant,
  onClose,
}) {
  // Bouton retour système (mobile) → ferme le panneau au lieu de quitter l'app.
  useBackToClose(Boolean(r), onClose)
  if (!r) return null

  const hasPriceHistoryChain = Array.isArray(r.price_history) && r.price_history.length > 1 && !!formatMarchePriceChainCompact(r.price_history)
  const showPriceHistorySummary =
    variant === 'baisse'
      ? hasPriceHistoryChain
      : variant === 'opportunite'
        ? hasPriceHistoryChain
        : false
  const baissesHistSteps = showPriceHistorySummary ? getMarcheHistorySteps(r.price_history) : []
  const ecMed = Number(r?.ecart_vs_mediane)
  const pillMed = ecartMarchePillStyle(ecMed)
  const alert = variant === 'baisse' ? marcheBaisseAlert(r) : null
  const subtitleParts = [
    r.first_registration_year,
    r.km != null ? `${Number(r.km).toLocaleString('fr-CH')} km` : null,
    r.model ? String(r.model).trim() : null,
    [r.canton, r.ville].filter(Boolean).join(' · ') || null,
  ].filter(Boolean)
  const modelHoverDetails = [formatVehicleDisplayTitle(r.titre), ...subtitleParts].filter(Boolean).join(' · ')

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Détail marché"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 4500,
        background: 'rgba(10,12,14,0.55)',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'stretch',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          maxWidth: '100vw',
          height: '100%',
          background: 'var(--surface)',
          borderLeft: '1px solid var(--border)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div
          style={{
            flexShrink: 0,
            padding: '16px 18px 12px',
            borderBottom: '1px solid rgba(148,163,184,0.14)',
            background: 'var(--surface)',
            zIndex: 3,
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0, width: 80 }}>
              {r.image_url || r.image_principale_url ? (
                <img
                  src={r.image_url || r.image_principale_url}
                  alt=""
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: RADIUS_BLOCK,
                    background: 'var(--surface-alt)',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: RADIUS_BLOCK,
                    background: 'var(--surface-alt)',
                    border: '1px solid var(--border)',
                  }}
                />
              )}
              {r.url ? (
                <a
                  href={r.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'block', marginTop: 6, fontSize: 12, fontWeight: 600, color: '#93c5fd', textDecoration: 'none' }}
                >
                  Ouvrir l&apos;annonce
                </a>
              ) : null}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingRight: 36, position: 'relative' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500, lineHeight: 1.25 }}>
                {r.url ? (
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noreferrer"
                    title={modelHoverDetails}
                    style={{ color: 'var(--text)', textDecoration: 'none', cursor: 'pointer' }}
                  >
                    {formatVehicleDisplayTitle(r.titre)}
                  </a>
                ) : (
                  <span style={{ color: 'var(--text)' }} title={modelHoverDetails}>
                    {formatVehicleDisplayTitle(r.titre)}
                  </span>
                )}
              </h2>
              {subtitleParts.length ? (
                <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                  {subtitleParts.join(' · ')}
                </p>
              ) : null}
              <button
                type="button"
                aria-label="Fermer"
                onClick={onClose}
                className="drawer-close-btn"
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  borderRadius: RADIUS_BLOCK,
                  border: '1px solid var(--border)',
                  background: 'rgba(255,255,255,0.04)',
                  color: 'var(--text)',
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 18px 20px' }}>
          {(r.positionnement_marche != null || Number.isFinite(ecMed)) && (
            <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
              {r.positionnement_marche ? (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: RADIUS_PILL,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    border: '1px solid rgba(148,163,184,0.22)',
                    background: 'rgba(255,255,255,0.06)',
                    color: 'rgba(226,232,240,0.85)',
                  }}
                >
                  {String(r.positionnement_marche)}
                </span>
              ) : null}
              {Number.isFinite(ecMed) ? (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: RADIUS_PILL,
                    fontSize: 12,
                    fontWeight: 500,
                    ...pillMed,
                  }}
                >
                  {ecMed > 0 ? '+' : ''}
                  {ecMed}% vs médiane
                </span>
              ) : null}
            </div>
          )}

          {alert ? (
            <div
              style={{
                marginTop: 16,
                padding: '12px 14px',
                borderLeft: alert.border,
                borderRadius: '0 6px 6px 0',
                background: alert.bg,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{alert.title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{alert.body}</div>
            </div>
          ) : null}

          {variant === 'baisse' ? (
            <>
              <SectionRule />
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 28, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em' }}>
                  {r.nouveau_prix != null ? formatChf(r.nouveau_prix) : '—'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'baseline', marginTop: 8 }}>
                  <span style={{ fontSize: 14, color: 'var(--text-tertiary)', textDecoration: 'line-through' }}>
                    {r.ancien_prix != null ? formatChf(r.ancien_prix) : '—'}
                  </span>
                  {r.delta != null ? (
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#fca5a5' }}>{Number(r.delta).toLocaleString('fr-CH')} CHF</span>
                  ) : null}
                </div>
              </div>
              {showPriceHistorySummary ? (
                <div
                  style={{
                    marginTop: 14,
                    padding: '12px 14px',
                    borderRadius: RADIUS_BLOCK,
                    border: '1px solid rgba(248,113,113,0.28)',
                    background: 'rgba(248,113,113,0.06)',
                  }}
                >
                  <div style={{ fontSize: 10, color: '#fecaca', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 6 }}>
                    Synthèse historique des prix
                  </div>
                  <div style={{ fontSize: 12, color: '#fca5a5', lineHeight: 1.45 }}>{formatMarchePriceChainCompact(r.price_history)}</div>
                  {baissesHistSteps.length > 0 ? (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(248,113,113,0.22)' }}>
                      {baissesHistSteps.map((row) => (
                        <div key={row.n} style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                          <div>
                            <span style={{ color: '#94a3b8', fontWeight: 600 }}>{row.n}.</span>{' '}
                            <span style={{ color: 'var(--text)' }}>
                              {row.from} → {row.to}
                            </span>{' '}
                            <span style={{ fontWeight: 600, color: row.isDrop ? '#f87171' : '#94a3b8' }}>({row.delta})</span>
                          </div>
                          {row.dateLabel ? (
                            <div
                              style={{
                                marginTop: 4,
                                marginLeft: 14,
                                fontSize: 11,
                                fontWeight: 500,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: 'rgba(148,163,184,0.9)',
                              }}
                            >
                              {row.dateLabel}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}

          {variant === 'vendu' ? (
            <>
              <SectionRule />
              <div style={{ fontSize: 28, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em' }}>
                {r.prix_chf != null ? formatChf(r.prix_chf) : '—'}
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                {r.disparue_at ? `Vente le ${new Date(r.disparue_at).toLocaleDateString('fr-CH')}` : null}
              </p>
            </>
          ) : null}

          {variant === 'nouvelle' ? (
            <>
              <SectionRule />
              <div style={{ fontSize: 28, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em' }}>
                {r.prix_chf != null ? formatChf(r.prix_chf) : '—'}
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                {r.premiere_vue_at ? `Première vue ${new Date(r.premiere_vue_at).toLocaleDateString('fr-CH')}` : null}
              </p>
            </>
          ) : null}

          {variant === 'opportunite' ? (
            <>
              <SectionRule />
              <div style={{ fontSize: 28, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em' }}>
                {r.prix_chf != null ? formatChf(r.prix_chf) : '—'}
              </div>
              {Number.isFinite(Number(r.ecart_pct)) ? (
                <span
                  style={{
                    display: 'inline-block',
                    marginTop: 10,
                    padding: '4px 10px',
                    borderRadius: RADIUS_PILL,
                    fontSize: 12,
                    fontWeight: 500,
                    color: Number(r.ecart_pct) <= -15 ? '#86efac' : '#4ade80',
                    border: '1px solid rgba(74,222,128,0.35)',
                    background: 'rgba(74,222,128,0.08)',
                  }}
                >
                  {Number(r.ecart_pct).toFixed(1)} % sous la fourchette de référence
                </span>
              ) : null}
              {cotationReferenceLabel(r) ? (
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-tertiary)' }}>{cotationReferenceLabel(r)}</div>
              ) : null}
              {(() => {
                const oppScale = scaleFromFourchetteRecord(r)
                const px = r.prix_chf != null ? Number(r.prix_chf) : null
                if (!oppScale || !Number.isFinite(px)) return null
                return (
                  <div style={{ marginTop: 16 }}>
                    <MarketPositionScale
                      prix={px}
                      scale={oppScale}
                      dotLabel="Cette annonce"
                      title="Position dans la fourchette"
                      axisLabels={{ left: 'Plancher', center: 'Référence', right: 'Plafond' }}
                    />
                  </div>
                )
              })()}
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.45 }}>
                  {Number(r.ecart_pct) <= -10 ? 'Fenêtre d’achat favorable.' : 'Opportunité modérée.'}
                </p>
                {r.baisse_recente != null ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      alignSelf: 'flex-start',
                      padding: '6px 10px',
                      borderRadius: 4,
                      border: '1px solid rgba(248,113,113,0.38)',
                      background: 'rgba(127,29,29,0.18)',
                      color: '#fecaca',
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.35,
                      boxSizing: 'border-box',
                    }}
                  >
                    Baisse récente : −{Number(r.baisse_recente).toLocaleString('fr-CH')} CHF (14 j)
                  </span>
                ) : null}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                {Number(r.confiance) > 0 ? (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      borderRadius: RADIUS_PILL,
                      padding: '4px 8px',
                      border: '1px solid rgba(0,30,80,0.35)',
                      background: 'rgba(0,30,80,0.14)',
                      color: '#bfdbfe',
                    }}
                  >
                    Confiance {Number(r.confiance)} %
                  </span>
                ) : null}
                {r.label_liquidite ? (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      borderRadius: RADIUS_PILL,
                      padding: '4px 8px',
                      border: '1px solid var(--border)',
                      background: 'rgba(255,255,255,0.04)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {r.label_liquidite}
                  </span>
                ) : null}
                {r.garantie ? (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      borderRadius: RADIUS_PILL,
                      padding: '4px 8px',
                      border: '1px solid rgba(74,222,128,0.25)',
                      background: 'rgba(74,222,128,0.1)',
                      color: '#4ade80',
                    }}
                  >
                    Garantie
                  </span>
                ) : null}
              </div>
              {showPriceHistorySummary ? (
                <div
                  style={{
                    marginTop: 14,
                    padding: '12px 14px',
                    borderRadius: RADIUS_BLOCK,
                    border: '1px solid rgba(248,113,113,0.28)',
                    background: 'rgba(248,113,113,0.06)',
                  }}
                >
                  <div style={{ fontSize: 10, color: '#fecaca', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 6 }}>
                    Synthèse historique des prix
                  </div>
                  <div style={{ fontSize: 12, color: '#fca5a5', lineHeight: 1.45 }}>{formatMarchePriceChainCompact(r.price_history)}</div>
                  {baissesHistSteps.length > 0 ? (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(248,113,113,0.22)' }}>
                      {baissesHistSteps.map((row) => (
                        <div key={row.n} style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                          <div>
                            <span style={{ color: '#94a3b8', fontWeight: 600 }}>{row.n}.</span>{' '}
                            <span style={{ color: 'var(--text)' }}>
                              {row.from} → {row.to}
                            </span>{' '}
                            <span style={{ fontWeight: 600, color: row.isDrop ? '#f87171' : '#94a3b8' }}>({row.delta})</span>
                          </div>
                          {row.dateLabel ? (
                            <div
                              style={{
                                marginTop: 4,
                                marginLeft: 14,
                                fontSize: 11,
                                fontWeight: 500,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: 'rgba(148,163,184,0.9)',
                              }}
                            >
                              {row.dateLabel}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}

          <SectionRule />
          <MarchePanelInsights
            token={token}
            record={r}
            variant={variant}
            showPositionMarcheIntro={false}
            compactTop
            omitHistoriqueChart
          />
        </div>
      </div>
    </div>
  )
}
